/**
 * Screen 21 — Admin · AI Question Studio.
 *
 * Three independent zones, top to bottom:
 *   1. Generate & auto-publish — pick category/difficulty/count, run Claude.
 *   2. Categories — add new ones; current list shown inline.
 *   3. Bank browser — filter by category + status, paginate. Newly generated
 *      items are prepended at the top of the current view and visibly tagged
 *      so it's clear they were just produced.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape, setStatus, statusZone } from '../components/form.js';
import { navigate } from '../router.js';

const STATUS_ID = 'studio-status';
const DIFFICULTIES = ['easy', 'medium', 'hard'];
const STATUS_FILTERS = ['all', 'published', 'rejected'];
const PAGE_SIZES = [10, 25, 50, 100];

// Bank view state — kept on the module so a re-render preserves the page.
const state = {
  categories: [],
  filterCategory: '', // '' === all categories
  filterStatus: 'all',
  pageSize: 25,
  offset: 0,
  hasMore: false,
};

export async function renderAdminQuestions(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div class="appbar"><div class="appbar-title">AI Question Studio</div></div>
      <div class="pad"><div class="card" style="color:var(--color-danger)">Admins only.</div></div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">AI Question Studio</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill ok">⚡ CLAUDE</span>
    </div>
    <div class="pad" id="studio-page">
      <div class="card"><div class="muted">Loading…</div></div>
    </div>
  `;

  try {
    state.categories = await api('/admin/categories');
  } catch (err) {
    const page = document.getElementById('studio-page');
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    page.innerHTML = `<div class="card" style="color:var(--color-danger)">${escape(err.message)}</div>`;
    return;
  }

  renderShell();
  await loadBank();
}

function renderShell() {
  const page = document.getElementById('studio-page');
  const { categories } = state;
  page.innerHTML = `
    <!-- Generate -->
    <div class="card">
      <h2 class="card-title">Generate &amp; auto-publish</h2>
      <form id="gen-form" class="form" novalidate>
        <div class="grid cols-3">
          <label class="field">
            <span class="field-label">Category</span>
            <select class="field-input" name="category_id" id="gen-cat" required>
              ${categories.length
                ? categories.map((c) => `<option value="${escape(c.id)}">${escape(c.name)}</option>`).join('')
                : `<option value="" disabled>(none — add one below)</option>`}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Difficulty</span>
            <select class="field-input" name="difficulty">
              ${DIFFICULTIES.map(
                (d) => `<option value="${d}" ${d === 'medium' ? 'selected' : ''}>${d}</option>`,
              ).join('')}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Count</span>
            <input class="field-input" name="count" type="number" min="1" max="25" value="5" required />
          </label>
        </div>
        ${statusZone(STATUS_ID)}
        <button type="submit" class="button-gold">✨ Generate &amp; auto-publish</button>
        <div class="muted" style="font-size: var(--fs-xs); text-align:center;">
          Each item is verified by a 2nd Claude pass, then published or rejected.
        </div>
      </form>
    </div>

    <!-- Categories -->
    <div class="card">
      <h2 class="card-title">Categories</h2>
      <form id="cat-form" class="form" novalidate>
        <div class="grid cols-2">
          <label class="field">
            <span class="field-label">Slug</span>
            <input class="field-input" name="slug" type="text" required pattern="[a-z0-9_-]+" placeholder="e.g. naija-pop" />
          </label>
          <label class="field">
            <span class="field-label">Display name</span>
            <input class="field-input" name="name" type="text" required placeholder="e.g. Naija Pop" />
          </label>
        </div>
        <button type="submit" class="button-secondary">Add category</button>
      </form>
      <div class="muted" style="font-size: var(--fs-xs);">${
        categories.length
          ? categories.map((c) => escape(c.name)).join(' · ')
          : 'No categories yet.'
      }</div>
    </div>

    <!-- Bank browser -->
    <h2 class="card-title" style="margin-top: var(--space-5);">Bank</h2>
    <div class="card">
      <form id="filter-form" class="form" style="margin: 0;" novalidate>
        <div class="grid cols-3">
          <label class="field">
            <span class="field-label">Category</span>
            <select class="field-input" id="filter-cat">
              <option value="">All categories</option>
              ${categories.map((c) => `<option value="${escape(c.id)}" ${c.id === state.filterCategory ? 'selected' : ''}>${escape(c.name)}</option>`).join('')}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Status</span>
            <select class="field-input" id="filter-status">
              ${STATUS_FILTERS.map((s) => `<option value="${s}" ${s === state.filterStatus ? 'selected' : ''}>${s}</option>`).join('')}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Per page</span>
            <select class="field-input" id="filter-size">
              ${PAGE_SIZES.map((n) => `<option value="${n}" ${n === state.pageSize ? 'selected' : ''}>${n}</option>`).join('')}
            </select>
          </label>
        </div>
      </form>
    </div>

    <div id="bank" class="card">
      <div class="muted">Loading…</div>
    </div>

    <div id="pager" class="row" style="justify-content: space-between; margin-top: var(--space-3);"></div>
  `;

  document.getElementById('gen-form').addEventListener('submit', onGenerate);
  document.getElementById('cat-form').addEventListener('submit', onCreateCategory);

  document.getElementById('filter-cat').addEventListener('change', (e) => {
    state.filterCategory = e.target.value;
    state.offset = 0;
    void loadBank();
  });
  document.getElementById('filter-status').addEventListener('change', (e) => {
    state.filterStatus = e.target.value;
    state.offset = 0;
    void loadBank();
  });
  document.getElementById('filter-size').addEventListener('change', (e) => {
    state.pageSize = Number(e.target.value);
    state.offset = 0;
    void loadBank();
  });
}

async function loadBank() {
  const host = document.getElementById('bank');
  host.innerHTML = `<div class="muted">Loading…</div>`;
  try {
    // Fetch limit+1 to detect "is there another page?" without a separate count call.
    const params = new URLSearchParams({
      limit: String(state.pageSize + 1),
      offset: String(state.offset),
    });
    if (state.filterCategory) params.set('category_id', state.filterCategory);
    if (state.filterStatus !== 'all') params.set('status', state.filterStatus);
    const items = await api(`/admin/questions?${params.toString()}`);
    state.hasMore = items.length > state.pageSize;
    const pageItems = items.slice(0, state.pageSize);
    renderBank(host, pageItems);
    renderPager();
  } catch (err) {
    host.innerHTML = `<div style="color:var(--color-danger)">${escape(err.message || 'Could not load bank.')}</div>`;
    document.getElementById('pager').innerHTML = '';
  }
}

function renderBank(host, items) {
  if (!items.length) {
    host.innerHTML = `<div class="muted">No questions match these filters.</div>`;
    return;
  }
  const catName = (id) => state.categories.find((c) => c.id === id)?.name || id?.slice(0, 8);
  host.innerHTML = `
    <div class="muted" style="font-size: var(--fs-xs); margin-bottom: var(--space-3);">
      Showing ${state.offset + 1}–${state.offset + items.length}${state.hasMore ? '+' : ''}. Newest first.
    </div>
    <ul class="txn-list" id="bank-list">
      ${items.map((q) => renderQuestionRow(q, { categoryName: catName(q.category_id) })).join('')}
    </ul>
  `;
  wireRemove(host);
  wireImageAttach(host);
}

function renderPager() {
  const pager = document.getElementById('pager');
  if (!pager) return;
  const start = state.offset + 1;
  const onFirst = state.offset === 0;
  pager.innerHTML = `
    <button type="button" class="button-secondary button-inline" id="prev-page" ${onFirst ? 'disabled' : ''}>← Prev</button>
    <div class="muted" style="font-size: var(--fs-xs);">From #${start}</div>
    <button type="button" class="button-secondary button-inline" id="next-page" ${state.hasMore ? '' : 'disabled'}>Next →</button>
  `;
  pager.querySelector('#prev-page').addEventListener('click', () => {
    state.offset = Math.max(0, state.offset - state.pageSize);
    void loadBank();
  });
  pager.querySelector('#next-page').addEventListener('click', () => {
    state.offset += state.pageSize;
    void loadBank();
  });
}

/**
 * Prepend newly-generated outcomes to the visible bank list.
 *
 * If the filter doesn't already match the just-generated batch's category,
 * we switch the filter to that category first so the user actually sees what
 * they produced; then we PREPEND each persisted item to the top of the list
 * with a gold left-border highlight. Items without a persisted question
 * (dupes, malformed) render as muted ghost rows so the admin sees what got
 * filtered upstream.
 */
function applyGenerationOutcomes(outcomes, generatedCategoryId) {
  if (!outcomes.length) return;

  // If the filter is on a specific category but not the one we just generated for,
  // switch it so the new items actually show up.
  if (state.filterCategory && state.filterCategory !== generatedCategoryId) {
    state.filterCategory = generatedCategoryId;
    state.offset = 0;
    const filterCat = document.getElementById('filter-cat');
    if (filterCat) filterCat.value = generatedCategoryId;
    void loadBank();
    return;
  }
  // Same constraint for status: if the user is filtering "rejected only" but
  // we just published items, swap to "all" so they see the publishes.
  const anyPublished = outcomes.some((o) => o.status === 'published');
  if (state.filterStatus === 'rejected' && anyPublished) {
    state.filterStatus = 'all';
    state.offset = 0;
    const filterStatus = document.getElementById('filter-status');
    if (filterStatus) filterStatus.value = 'all';
    void loadBank();
    return;
  }
  // Not on first page? Jump there — newest items are at offset=0 by definition.
  if (state.offset > 0) {
    state.offset = 0;
    void loadBank();
    return;
  }

  const host = document.getElementById('bank');
  let list = host.querySelector('#bank-list');
  if (!list) {
    host.innerHTML = `<ul class="txn-list" id="bank-list"></ul>`;
    list = host.querySelector('#bank-list');
  }
  const catName = (id) => state.categories.find((c) => c.id === id)?.name || id?.slice(0, 8);
  const rows = outcomes
    .map((o) => {
      if (o.question) {
        return renderQuestionRow(o.question, {
          highlight: true,
          verdictNote: outcomeNote(o),
          categoryName: catName(o.question.category_id),
        });
      }
      return renderEphemeralOutcome(o);
    })
    .join('');
  list.insertAdjacentHTML('afterbegin', rows);
  wireRemove(host);
  wireImageAttach(host);
}

function outcomeNote(o) {
  const conf = typeof o.confidence === 'number' ? ` · ${(o.confidence * 100).toFixed(0)}% conf` : '';
  return `${o.status}${conf}${o.reason ? ` — ${o.reason}` : ''}`;
}

function renderEphemeralOutcome(o) {
  return `
    <li class="txn-row" style="opacity: 0.65; background: var(--color-surface-muted);">
      <div class="txn-row-main">
        <div class="txn-row-kind">(${escape(o.status)})</div>
        <div class="txn-row-sub">${escape(o.reason || '')}</div>
      </div>
      <div class="txn-row-side">
        <span class="status-pill warn">${escape(o.status)}</span>
      </div>
    </li>
  `;
}

function renderQuestionRow(q, opts = {}) {
  const statusClass = q.status === 'published' ? 'ok' : 'bad';
  const correct = q.choices?.[q.answer_index];
  const conf =
    typeof q.verify_confidence === 'number'
      ? ` · ${(q.verify_confidence * 100).toFixed(0)}% conf`
      : '';
  const note = opts.verdictNote
    ? `<div class="txn-row-sub" style="margin-top:2px;">${escape(opts.verdictNote)}</div>`
    : q.verify_reason
      ? `<div class="txn-row-sub" style="margin-top:2px;">${escape(q.verify_reason)}${conf}</div>`
      : '';
  const ring = opts.highlight
    ? 'border-left: 3px solid var(--color-accent); padding-left: var(--space-3);'
    : '';
  const catBadge = opts.categoryName
    ? `<span class="muted" style="font-size: var(--fs-xs); margin-right: var(--space-2);">${escape(opts.categoryName)}</span>`
    : '';
  const thumb = q.image_url
    ? `<img src="${escape(q.image_url)}" alt="" loading="lazy" style="width:42px;height:42px;object-fit:cover;border-radius:6px;margin-right:8px;flex-shrink:0;" />`
    : '';
  return `
    <li class="txn-row" data-id="${escape(q.id)}" style="${ring}">
      <div class="txn-row-main" style="display:flex;align-items:flex-start;">
        ${thumb}
        <div style="flex:1;min-width:0;">
          <div class="txn-row-kind">${catBadge}${escape(q.prompt)}</div>
          <div class="txn-row-sub">
            ✓ ${escape(correct || '(no correct option)')} · ${escape(q.difficulty || '')}
          </div>
          ${note}
        </div>
      </div>
      <div class="txn-row-side">
        <span class="status-pill ${statusClass}">${escape(q.status)}</span>
        ${q.status === 'published' ? `
          <button type="button" class="link-button image-q" data-id="${escape(q.id)}" data-image-url="${escape(q.image_url || '')}">
            ${q.image_url ? '🖼️ Replace' : '🖼️ Add image'}
          </button>
          <button type="button" class="link-button danger remove-q" data-id="${escape(q.id)}">Remove</button>
        ` : ''}
      </div>
    </li>
  `;
}

// Inline image attach: clicking opens a small dialog with a file picker and
// a URL field. Uploaded file goes through Pillow + S3 server-side and
// returns a CDN URL; pasted URL is used as-is (e.g. flagcdn.com).
function wireImageAttach(host) {
  host.querySelectorAll('.image-q').forEach((btn) => {
    if (btn.dataset.wired === '1') return;
    btn.dataset.wired = '1';
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      const current = btn.dataset.imageUrl || '';
      await openImageDialog({ questionId: id, currentUrl: current });
    });
  });
}

async function openImageDialog({ questionId, currentUrl }) {
  // Cheap modal; reuses the toast layer so we don't drag in a dialog dep.
  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:1000;display:flex;align-items:center;justify-content:center;padding:18px;';
  wrap.innerHTML = `
    <div class="card" style="max-width:420px;width:100%;background:var(--color-surface);">
      <div class="card-title">Question image</div>
      <p class="muted" style="font-size: var(--fs-sm);margin-bottom:12px;">
        Pick a file <i>or</i> paste a URL. Files are resized to ≤1024px
        and stored on our CDN; URLs are used as-is.
      </p>
      ${currentUrl ? `<img src="${currentUrl}" alt="" style="width:100%;border-radius:8px;margin-bottom:10px;" />` : ''}
      <label class="field">
        <span class="field-label">Upload</span>
        <input type="file" name="file" accept="image/*" class="field-input" />
      </label>
      <label class="field">
        <span class="field-label">…or paste a URL</span>
        <input type="url" name="url" class="field-input" placeholder="https://flagcdn.com/ng.svg" value="${escape(currentUrl)}" />
      </label>
      <div id="img-status" style="font-size: var(--fs-xs);min-height:1em;margin:6px 0;"></div>
      <div class="form-actions">
        ${currentUrl ? '<button type="button" class="link-button danger" data-action="clear">Remove image</button>' : ''}
        <button type="button" class="button-secondary" data-action="cancel">Cancel</button>
        <button type="button" class="button-gold" data-action="save">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(wrap);
  const setStatus = (msg, kind = 'info') => {
    const el = wrap.querySelector('#img-status');
    el.textContent = msg || '';
    el.style.color = kind === 'error' ? 'var(--color-danger)' : 'var(--muted)';
  };
  const close = () => wrap.remove();
  wrap.addEventListener('click', (e) => { if (e.target === wrap) close(); });

  wrap.querySelector('[data-action="cancel"]').addEventListener('click', close);
  const clearBtn = wrap.querySelector('[data-action="clear"]');
  if (clearBtn) clearBtn.addEventListener('click', async () => {
    try {
      await api(`/admin/questions/${questionId}`, { method: 'PATCH', body: { image_url: '' } });
      close();
      renderAdminQuestions(document.getElementById('route-shell'));
    } catch (err) { setStatus(err.message || 'Could not clear image.', 'error'); }
  });
  wrap.querySelector('[data-action="save"]').addEventListener('click', async () => {
    const fileEl = wrap.querySelector('input[name="file"]');
    const urlEl = wrap.querySelector('input[name="url"]');
    let imageUrl = (urlEl.value || '').trim();
    if (fileEl.files && fileEl.files[0]) {
      setStatus('Uploading…');
      try {
        const fd = new FormData();
        fd.append('image', fileEl.files[0]);
        const res = await api('/admin/questions/upload-image', {
          method: 'POST',
          body: fd,
        });
        imageUrl = res.url;
      } catch (err) {
        setStatus(err.message || 'Upload failed.', 'error');
        return;
      }
    }
    if (!imageUrl) {
      setStatus('Pick a file or paste a URL.', 'error');
      return;
    }
    try {
      await api(`/admin/questions/${questionId}`, {
        method: 'PATCH',
        body: { image_url: imageUrl },
      });
      close();
      renderAdminQuestions(document.getElementById('route-shell'));
    } catch (err) {
      setStatus(err.message || 'Could not save.', 'error');
    }
  });
}

function wireRemove(host) {
  host.querySelectorAll('.remove-q').forEach((btn) => {
    if (btn.dataset.wired === '1') return;
    btn.dataset.wired = '1';
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      if (!confirm('Remove this question from the bank?')) return;
      btn.disabled = true;
      try {
        await api(`/admin/questions/${id}`, { method: 'DELETE' });
        const row = btn.closest('li');
        const pill = row.querySelector('.status-pill');
        if (pill) {
          pill.className = 'status-pill bad';
          pill.textContent = 'rejected';
        }
        btn.remove();
      } catch (err) {
        btn.disabled = false;
        alert(err.message || 'Could not remove.');
      }
    });
  });
}

async function onGenerate(e) {
  e.preventDefault();
  const form = e.currentTarget;
  const data = Object.fromEntries(new FormData(form).entries());
  data.count = Number(data.count);
  if (!data.category_id) {
    setStatus(STATUS_ID, { message: 'Pick a category first.', kind: 'error' });
    return;
  }
  const submit = form.querySelector('button[type="submit"]');
  submit.disabled = true;
  setStatus(STATUS_ID, {
    message: `Generating ${data.count} ${data.difficulty} question(s)…`,
    kind: 'info',
  });
  try {
    const body = await api('/admin/questions/generate', {
      method: 'POST',
      body: {
        category_id: data.category_id,
        difficulty: data.difficulty,
        count: data.count,
        locale: 'en-NG',
      },
    });
    const job = body.job;
    // When the AI call fails entirely, surface the error so admins know
    // (otherwise they see "0 published" with no clue why — Claude billing,
    // network, etc). The backend already converts SDK errors into operator-
    // friendly sentences, so we can show them verbatim.
    if (job.status === 'failed') {
      const detail = job.error || 'AI provider rejected the request.';
      setStatus(STATUS_ID, { message: detail, kind: 'error' });
      return;
    }
    const summary = `${job.published_count} published · ${job.rejected_count} rejected · ${job.duplicate_count} dup · ${job.malformed_count} malformed`;
    setStatus(STATUS_ID, { message: summary, kind: 'success' });
    applyGenerationOutcomes(body.outcomes, data.category_id);
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Generation failed.';
    setStatus(STATUS_ID, { message: msg, kind: 'error' });
  } finally {
    submit.disabled = false;
  }
}

async function onCreateCategory(e) {
  e.preventDefault();
  const form = e.currentTarget;
  const data = Object.fromEntries(new FormData(form).entries());
  const submit = form.querySelector('button[type="submit"]');
  submit.disabled = true;
  try {
    await api('/admin/categories', {
      method: 'POST',
      body: { slug: data.slug, name: data.name, locale: 'en-NG' },
    });
    form.reset();
    renderAdminQuestions(document.getElementById('route-shell'));
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Could not add category.';
    alert(msg);
  } finally {
    submit.disabled = false;
  }
}
